import { api, ApiError } from "./api";
import { esc } from "./dom";
import "./weekly-preparation.css";

export interface WeeklyContext { classId: string; termId: string; weekNo: number; nodeId?: string | null; lessonId?: string | null }
export interface WeeklyTerm { id: string; name: string; start_date: string; end_date: string; week_count: number; ay: string | null }
export interface WeeklyLesson {
  id: string; class_id: string; class_name: string; topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  plan_id: string | null; node_id: string | null; strand: string | null; slot: number | null; can_open: boolean;
}
export interface WeeklyPreparation {
  class: { id: string; name: string; level: string | null; teacher_id: string; periods_per_week: number };
  term: WeeklyTerm; week_no: number;
  declaration: null | { lesson_count: number; revision: number; author_id: string; created_at: string; updated_by: string; updated_at: string };
  editable: boolean; timetable_suggestion: number;
  term_totals: { lesson_count: number; entered_weeks: number; available_weeks: number };
  year_totals: null | { ay: string; lesson_count: number; entered_weeks: number; available_weeks: number };
  available_terms: WeeklyTerm[]; lessons: WeeklyLesson[];
}

export function weeklyQuery(ctx: WeeklyContext): URLSearchParams {
  const q = new URLSearchParams({ class: ctx.classId, term: ctx.termId, week: String(ctx.weekNo) });
  if (ctx.nodeId) q.set("node", ctx.nodeId);
  if (ctx.lessonId) q.set("lesson", ctx.lessonId);
  return q;
}
export function weeklyHref(ctx: WeeklyContext): string { return `/this-week?${weeklyQuery(ctx)}`; }
export function weeklyApiPath(ctx: WeeklyContext): string {
  return `/api/classes/${encodeURIComponent(ctx.classId)}/weekly-preparation?${new URLSearchParams({ term_id: ctx.termId, week_no: String(ctx.weekNo) })}`;
}
export const loadWeeklyPreparation = (ctx: WeeklyContext): Promise<WeeklyPreparation> => api<WeeklyPreparation>(weeklyApiPath(ctx));

function totalsHtml(data: WeeklyPreparation): string {
  const line = (v: WeeklyPreparation["term_totals"]): string => `${v.lesson_count} lessons entered across ${v.entered_weeks} of ${v.available_weeks} weeks`;
  return `<div class="weekly-totals"><p><b>${esc(data.term.name)}:</b> ${line(data.term_totals)}</p>
    <p><b>${data.year_totals ? esc(data.year_totals.ay) : "Academic year"}:</b> ${data.year_totals ? line(data.year_totals) : "Total unavailable — this term has no academic year set."}</p></div>
    <p class="muted small">Only entered weeks are counted. These are scheduled lessons, separate from plans and teaching records.</p>`;
}

/** Fresh reads on each page; saves never change timetable, plans or teaching evidence. */
export async function mountWeeklyPreparation(host: HTMLElement, ctx: WeeklyContext, initial?: WeeklyPreparation, options: { link?: boolean; totalsOnly?: boolean } = {}): Promise<void> {
  let data = initial;
  let message = "";
  let conflict = false;
  let inputValue: string | undefined;
  async function load(): Promise<void> {
    host.innerHTML = `<p class="muted" role="status">Loading the saved lesson count…</p>`;
    try { data = await loadWeeklyPreparation(ctx); message = ""; conflict = false; draw(); }
    catch (error) {
      host.innerHTML = `<p role="alert">The saved lesson count could not be loaded. ${esc((error as Error).message)}</p><button type="button" class="btn secondary" data-week-retry>Retry lesson count</button>`;
      host.querySelector("[data-week-retry]")?.addEventListener("click", () => { void load(); });
    }
  }
  function draw(): void {
    if (!data) return;
    const saved = data.declaration?.lesson_count;
    host.classList.add("weekly-preparation");
    host.innerHTML = `<div class="weekly-count-heading"><h2>${options.totalsOnly ? "Lessons entered" : saved === undefined ? "How many lessons do you have this week?" : `This week you have ${saved} lesson${saved === 1 ? "" : "s"}.`}</h2>
      ${options.link ? `<a class="btn" data-link data-prepare-week href="${esc(weeklyHref(ctx))}">Prepare this week</a>` : ""}</div>
      <p class="muted small">${esc(data.class.name)} · ${esc(data.term.name)} · School week ${data.week_no}${saved === undefined ? " · No count entered yet" : " · Saved"}</p>
      ${!options.totalsOnly && data.editable ? `<form class="weekly-count-form" novalidate><label>Lessons this week <input name="lesson_count" type="number" inputmode="numeric" min="0" max="40" step="1" value="${esc(inputValue ?? (saved === undefined ? "" : String(saved)))}" aria-label="Lessons this week" aria-describedby="weekly-count-help-${esc(data.class.id)}"></label>
        <button type="submit" class="btn secondary"${conflict ? " disabled" : ""}>Save lesson count</button>
        <button type="button" class="linkbtn" data-use-timetable>Use timetable suggestion (${data.timetable_suggestion})</button>
        <p id="weekly-count-help-${esc(data.class.id)}" class="muted small">One lesson is one scheduled teaching period. Enter 0 for no lessons. The timetable suggestion is saved only when you choose Save.</p></form>` : ""}
      ${!data.editable && !options.totalsOnly ? `<p class="muted small">The assigned teacher can edit this count.</p>` : ""}
      <p class="weekly-count-message" role="status">${esc(message)}</p>
      ${conflict ? `<button class="btn secondary" type="button" data-reload-count>Reload saved count and keep my entry</button>` : ""}
      ${totalsHtml(data)}`;
    const input = host.querySelector<HTMLInputElement>('input[name="lesson_count"]');
    input?.addEventListener("input", () => { inputValue = input.value; });
    host.querySelector("[data-use-timetable]")?.addEventListener("click", () => { if (input) { input.value = String(data!.timetable_suggestion); inputValue = input.value; input.focus(); } });
    host.querySelector("[data-reload-count]")?.addEventListener("click", () => { void load(); });
    host.querySelector("form")?.addEventListener("submit", async (event) => {
      event.preventDefault();
      const raw = input?.value.trim() ?? "";
      if (!/^\d+$/.test(raw) || Number(raw) > 40) {
        message = "Enter a whole number from 0 to 40. Your last saved count is unchanged.";
        draw(); host.querySelector<HTMLInputElement>("input")?.focus(); return;
      }
      inputValue = raw;
      host.querySelectorAll<HTMLButtonElement | HTMLInputElement>("button,input").forEach((control) => { control.disabled = true; });
      host.querySelector(".weekly-count-message")!.textContent = "Saving lesson count…";
      try {
        data = await api<WeeklyPreparation>(weeklyApiPath(ctx), { method: "PUT", body: JSON.stringify({ lesson_count: Number(raw), revision: data!.declaration?.revision ?? 0 }) });
        inputValue = undefined; message = "Lesson count saved.";
      } catch (error) {
        conflict = error instanceof ApiError && error.status === 409;
        message = `${(error as Error).message} Your last loaded count is still shown above; your entry is kept below.${conflict ? " Reload before trying again." : " Try Save again."}`;
      }
      draw();
    });
  }
  if (data) draw(); else await load();
}
